import { addMark } from '@atlaskit/react-ufo/interaction-metrics';

import type { PageSegmentLoadMetric } from '@atlassian/browser-metrics/page-segment-load';

import {
	PAGE_SEGMENT_MARK_PREFIX_END,
	PAGE_SEGMENT_MARK_PREFIX_START,
} from './metric-keys/index.ts';
import { createBM3PageSegmentLoadMetricRaw } from './createBM3PageSegmentLoadMetricRaw.ts';
import {
	bindPageSegmentToUFO,
	getPageSegmentUFOBinding,
	reportPageSegmentEndToUFO,
} from './pageSegmentUFOInterop.ts';

type CreateConfig = Parameters<typeof createBM3PageSegmentLoadMetricRaw>[0];

/**
 * Keep BM3 for unlisted owners/segments and calls without a React or imperative start.
 * A listed owner and segment opt into the marks/data path through the shared start binding.
 * Capture only that destination: UFO retains lifecycle and reporting ownership.
 *
 * The raw instance remains necessary for legacy parents' instanceof checks. Its native
 * path does not complete a BM3 dependency. The View Page test scope must still offboard
 * its parent and validate event/attribute parity before production rollout.
 */
export function createPageSegmentLoadMetric(config: CreateConfig): PageSegmentLoadMetric {
	const metric = createBM3PageSegmentLoadMetricRaw(config);
	const key = config.key;

	// Capture the original methods before overriding them.
	const realStart = metric.start.bind(metric);
	const realStartFromPageLoad = metric.startFromPageLoad.bind(metric);
	const realStop = metric.stop.bind(metric);
	const realCancel = metric.cancel.bind(metric);
	const realMark = metric.mark.bind(metric);
	const realMarkFMP = metric.markFMP.bind(metric);

	metric.start = (...args: Parameters<PageSegmentLoadMetric['start']>) => {
		const binding = bindPageSegmentToUFO(metric, key, args[0]?.startTime);
		if (binding) {
			addMark(
				binding.interactionId,
				'custom',
				`${PAGE_SEGMENT_MARK_PREFIX_START}:${key}`,
				[],
				binding.startTime,
			);
			return;
		}
		return realStart(...args);
	};

	metric.startFromPageLoad = (...args: Parameters<PageSegmentLoadMetric['startFromPageLoad']>) => {
		const binding = bindPageSegmentToUFO(metric, key, 'page-load');
		if (binding) {
			addMark(
				binding.interactionId,
				'custom',
				`${PAGE_SEGMENT_MARK_PREFIX_START}:${key}`,
				[],
				binding.startTime,
			);
			return;
		}
		return realStartFromPageLoad(...args);
	};

	metric.stop = (...args: Parameters<PageSegmentLoadMetric['stop']>) => {
		const binding = getPageSegmentUFOBinding(metric);
		if (binding) {
			reportPageSegmentEndToUFO(binding, args[0]);
			return true;
		}
		return realStop(...args);
	};

	metric.cancel = (...args: Parameters<PageSegmentLoadMetric['cancel']>) => {
		const binding = getPageSegmentUFOBinding(metric);
		if (binding) {
			// This is a legacy marker, not a native interaction cancellation/completion.
			addMark(
				binding.interactionId,
				'custom',
				`${PAGE_SEGMENT_MARK_PREFIX_END}:${key}`,
				[],
				performance.now(),
			);
			return;
		}
		return realCancel(...args);
	};

	metric.mark = (...args: Parameters<PageSegmentLoadMetric['mark']>) => {
		const binding = getPageSegmentUFOBinding(metric);
		if (binding) {
			const [name, timestamp] = args;
			addMark(binding.interactionId, 'custom', name, [], timestamp ?? performance.now());
			return;
		}
		return realMark(...args);
	};

	metric.markFMP = (...args: Parameters<PageSegmentLoadMetric['markFMP']>) => {
		const binding = getPageSegmentUFOBinding(metric);
		if (binding) {
			const [timestamp] = args;
			addMark(binding.interactionId, 'custom', `fmp:${key}`, [], timestamp ?? performance.now());
			return;
		}
		return realMarkFMP(...args);
	};

	return metric;
}
